import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Wrench, Car, Sparkles, ArrowRight, Star, Truck, User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets-rejwkqb3.emergentagent.net/job_47615226-8be8-492e-9b64-c74bd12fbe55/artifacts/a0f8zaxp_02_MA_Africa_Detailing_Full_Colour_Transparent.webp";

// DETAIL first - it's the cash cow
const doors = [
  {
    id: 'detail',
    title: 'DETAIL',
    subtitle: 'Professional Detailing',
    description: 'Interior & exterior care',
    priceFrom: 'From R350',
    icon: Sparkles,
    path: '/detail',
    gradient: 'from-zinc-900 via-zinc-800 to-zinc-900',
    accentColor: '#C41E3A',
    image: 'https://images.unsplash.com/photo-1620584898989-d39f7f9ed1b7?w=800&q=80',
    featured: true
  },
  {
    id: 'fix',
    title: 'FIX',
    subtitle: 'Mechanical Repairs',
    description: 'Diagnostics, servicing & repairs',
    priceFrom: 'Get a Quote',
    icon: Wrench,
    path: '/fix',
    gradient: 'from-zinc-900 via-zinc-800 to-zinc-900',
    accentColor: '#C41E3A',
    image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=800&q=80',
    featured: false
  },
  {
    id: 'restore',
    title: 'RESTORE',
    subtitle: 'Panel Beating',
    description: 'Body repair & paintwork',
    priceFrom: 'Get a Quote',
    icon: Car,
    path: '/restore',
    gradient: 'from-zinc-900 via-zinc-800 to-zinc-900',
    accentColor: '#C41E3A',
    image: 'https://images.unsplash.com/photo-1786489785858-b64715400a37?w=800&q=80',
    featured: false
  }
];

export default function LandingDoors() {
  const navigate = useNavigate();
  const [hoveredDoor, setHoveredDoor] = useState(null);
  const [selectedDoor, setSelectedDoor] = useState(null);
  const [touchedDoor, setTouchedDoor] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { user, loading, login } = useAuth();

  const handleDoorClick = (door) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setSelectedDoor(door.id);
    
    // Navigate after animation completes
    setTimeout(() => {
      navigate(door.path);
    }, 900);
  };

  const handleTouchStart = (doorId) => {
    if (!isTransitioning) setTouchedDoor(doorId);
  };

  const handleTouchEnd = () => {
    setTouchedDoor(null);
  };

  return (
    <main 
      data-testid="landing-doors" 
      className="min-h-screen bg-black flex flex-col overflow-hidden"
    >
      {/* Header */}
      <motion.header 
        className="fixed top-0 left-0 right-0 z-30 px-4 md:px-6 py-4 md:py-6 bg-gradient-to-b from-black/80 to-transparent"
        animate={{ 
          opacity: isTransitioning ? 0 : 1,
          y: isTransitioning ? -20 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <div className="max-w-[1400px] mx-auto flex items-center justify-between">
          <img src={LOGO_URL} alt="MA Africa" className="h-8 md:h-12" />
          <div className="flex items-center gap-4">
            <p className="text-[10px] md:text-xs text-zinc-500 tracking-widest uppercase hidden sm:block">
              Choose Your Service
            </p>
            {/* Profile Icon / Login-Signup */}
            {!loading && (
              user ? (
                <button
                  onClick={() => navigate('/my-bookings')}
                  className="flex items-center transition-colors p-1 rounded-lg hover:bg-white/10"
                  data-testid="doors-profile-btn"
                >
                  {user?.picture ? (
                    <img 
                      src={user.picture} 
                      alt={user.name} 
                      className="w-8 h-8 rounded-full object-cover border-2 border-white/20"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-[#C41E3A] border border-[#C41E3A] flex items-center justify-center">
                      <span className="text-white text-xs font-bold">{user?.name?.charAt(0) || 'U'}</span>
                    </div>
                  )}
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="text-sm text-zinc-400 hover:text-white transition-colors px-2 py-1"
                    data-testid="doors-login-link"
                  >
                    Login
                  </Link>
                  <Link
                    to="/signup"
                    className="text-sm font-medium px-3 py-1.5 rounded-lg bg-[#C41E3A] text-white hover:bg-[#a01830] transition-colors"
                    data-testid="doors-signup-link"
                  >
                    Sign Up
                  </Link>
                </div>
              )
            )}
          </div>
        </div>
      </motion.header>

      {/* Fleet Services Banner - Fixed at bottom on desktop, top on mobile */}
      <motion.div 
        className="fixed left-0 right-0 z-40 md:bottom-16 md:top-auto top-16 bottom-auto px-4"
        animate={{ 
          opacity: isTransitioning ? 0 : 1,
          y: isTransitioning ? -20 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <Link
          to="/fleet-services"
          className="max-w-lg mx-auto bg-zinc-900/90 backdrop-blur-md border border-zinc-800 rounded-xl p-3 flex items-center gap-4 hover:bg-zinc-800/90 transition-colors block"
          data-testid="fleet-banner"
        >
          <div className="w-10 h-10 rounded-lg bg-[#C41E3A]/20 flex items-center justify-center flex-shrink-0">
            <Truck className="w-5 h-5 text-[#C41E3A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-white">Managing more than one vehicle?</p>
            <p className="text-[10px] text-zinc-400">Fleet Full Valet from R900/vehicle</p>
          </div>
          <ArrowRight className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </Link>
      </motion.div>

      {/* Mobile: Vertical Stack | Desktop: Horizontal */}
      <div className="flex-1 flex flex-col md:flex-row pt-16 md:pt-0">
        {doors.map((door, index) => {
          const isHovered = hoveredDoor === door.id;
          const isTouched = touchedDoor === door.id;
          const isActive = isHovered || isTouched;
          const isSelected = selectedDoor === door.id;
          const isOtherSelected = selectedDoor && selectedDoor !== door.id;
          
          return (
            <motion.div
              key={door.id}
              className={`relative cursor-pointer overflow-hidden ${
                door.featured ? 'min-h-[45vh] md:min-h-0' : 'min-h-[27.5vh] md:min-h-0'
              }`}
              onMouseEnter={() => !isTransitioning && setHoveredDoor(door.id)}
              onMouseLeave={() => setHoveredDoor(null)}
              onTouchStart={() => handleTouchStart(door.id)}
              onTouchEnd={handleTouchEnd}
              onClick={() => handleDoorClick(door)}
              initial={false}
              animate={{
                flex: isSelected ? 5 : isOtherSelected ? 0 : isHovered ? 1.4 : door.featured ? 1.2 : 1,
                opacity: isOtherSelected ? 0 : 1
              }}
              transition={{ 
                duration: isTransitioning ? 0.6 : 0.4, 
                ease: [0.25, 0.1, 0.25, 1] 
              }}
              data-testid={`door-${door.id}`}
            >
              {/* Background Image */}
              <motion.div 
                className="absolute inset-0"
                animate={{
                  scale: isSelected ? 1.2 : 1
                }}
                transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
              >
                <motion.img
                  src={door.image}
                  alt={door.title}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.05 }}
                  animate={{ 
                    scale: isActive ? 1.1 : 1.05,
                    filter: isSelected ? 'brightness(1.2)' : 'brightness(1)'
                  }}
                  transition={{ duration: 0.5 }}
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${door.gradient} opacity-75`} />
                <motion.div 
                  className="absolute inset-0 bg-black"
                  animate={{ 
                    opacity: isSelected ? 0 : isActive ? 0.15 : 0.35 
                  }}
                  transition={{ duration: 0.3 }}
                />
              </motion.div>

              {/* Featured Badge - Mobile */}
              {door.featured && !isTransitioning && (
                <motion.div 
                  className="absolute top-4 right-4 z-10 md:hidden"
                  animate={{ opacity: isOtherSelected ? 0 : 1 }}
                >
                  <div className="flex items-center gap-1 bg-[#C41E3A] text-white text-[10px] font-semibold px-2 py-1 rounded-full">
                    <Star className="w-3 h-3 fill-white" />
                    Popular
                  </div>
                </motion.div>
              )}

              {/* Door Border Frame */}
              <motion.div
                className="absolute inset-3 md:inset-5 border-2 rounded-xl pointer-events-none"
                animate={{ 
                  borderColor: isSelected 
                    ? 'rgba(196,30,58,0)' 
                    : isActive 
                      ? 'rgba(196,30,58,0.6)' 
                      : 'rgba(255,255,255,0.08)',
                  boxShadow: isActive && !isSelected ? 'inset 0 0 40px rgba(196,30,58,0.1)' : 'none',
                  scale: isSelected ? 1.1 : 1,
                  opacity: isSelected ? 0 : 1
                }}
                transition={{ duration: 0.4 }}
              />

              {/* Content */}
              <motion.div 
                className="relative h-full flex flex-col items-center justify-center px-4 md:px-6 py-8 md:py-16"
                animate={{
                  opacity: isSelected ? 0 : 1,
                  scale: isSelected ? 0.9 : 1,
                  y: isSelected ? -30 : 0
                }}
                transition={{ duration: 0.4 }}
              >
                {/* Icon */}
                <motion.div
                  className="mb-3 md:mb-5"
                  animate={{ 
                    scale: isActive ? 1.1 : 1,
                    y: isActive ? -5 : 0
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`w-12 h-12 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center ${
                    door.featured ? 'bg-[#C41E3A]/20' : 'bg-white/10'
                  } backdrop-blur-sm`}>
                    <door.icon className={`w-6 h-6 md:w-8 md:h-8 ${
                      door.featured ? 'text-[#C41E3A]' : 'text-white'
                    }`} />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h2
                  className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-1 text-center"
                  animate={{ 
                    scale: isActive ? 1.05 : 1,
                    color: isActive || door.featured ? '#C41E3A' : '#ffffff'
                  }}
                  transition={{ duration: 0.3 }}
                >
                  {door.title}
                </motion.h2>

                {/* Subtitle */}
                <motion.p
                  className="text-xs md:text-sm text-zinc-400 mb-1 text-center"
                  animate={{ opacity: isActive ? 1 : 0.8 }}
                >
                  {door.subtitle}
                </motion.p>

                {/* Price/CTA */}
                <motion.p
                  className="text-[10px] md:text-xs text-zinc-500 text-center mt-1"
                >
                  {door.priceFrom}
                </motion.p>

                {/* Description - Desktop hover */}
                <motion.p
                  className="text-[11px] md:text-xs text-zinc-500 text-center max-w-[180px] mt-2 hidden md:block"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ 
                    opacity: isActive ? 1 : 0,
                    y: isActive ? 0 : 5
                  }}
                  transition={{ duration: 0.2 }}
                >
                  {door.description}
                </motion.p>

                {/* Enter Button */}
                <motion.div
                  className="mt-4 md:mt-6"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: isActive || door.featured ? 1 : 0,
                    y: isActive || door.featured ? 0 : 10
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <div className={`flex items-center gap-2 text-white text-xs md:text-sm font-medium px-5 py-2.5 md:px-6 md:py-3 rounded-full ${
                    door.featured ? 'bg-[#C41E3A]' : 'bg-white/20 backdrop-blur-sm'
                  }`}>
                    Enter
                    <ArrowRight className="w-3.5 h-3.5 md:w-4 md:h-4" />
                  </div>
                </motion.div>

                {/* Door Number - Desktop only */}
                <div className="absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 hidden md:block">
                  <span className="text-[10px] text-zinc-700 tracking-widest">
                    0{index + 1}
                  </span>
                </div>
              </motion.div>

              {/* Door Opening Animation - Split doors effect */}
              <AnimatePresence>
                {isSelected && (
                  <>
                    {/* Left door panel */}
                    <motion.div
                      className="absolute inset-y-0 left-0 w-1/2 bg-zinc-950 z-20"
                      initial={{ x: 0, skewY: 0 }}
                      animate={{ 
                        x: '-100%',
                        rotateY: -30,
                      }}
                      transition={{ 
                        duration: 0.7, 
                        ease: [0.45, 0, 0.15, 1],
                        delay: 0.1
                      }}
                      style={{ 
                        transformOrigin: 'left center',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-zinc-900 to-zinc-950" />
                      {/* Door edge highlight */}
                      <div className="absolute right-0 inset-y-0 w-1 bg-[#C41E3A]/50" />
                    </motion.div>
                    
                    {/* Right door panel */}
                    <motion.div
                      className="absolute inset-y-0 right-0 w-1/2 bg-zinc-950 z-20"
                      initial={{ x: 0, skewY: 0 }}
                      animate={{ 
                        x: '100%',
                        rotateY: 30,
                      }}
                      transition={{ 
                        duration: 0.7, 
                        ease: [0.45, 0, 0.15, 1],
                        delay: 0.1
                      }}
                      style={{ 
                        transformOrigin: 'right center',
                        transformStyle: 'preserve-3d'
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-l from-zinc-900 to-zinc-950" />
                      {/* Door edge highlight */}
                      <div className="absolute left-0 inset-y-0 w-1 bg-[#C41E3A]/50" />
                    </motion.div>

                    {/* Center line glow */}
                    <motion.div
                      className="absolute inset-y-0 left-1/2 w-px bg-[#C41E3A] z-30"
                      initial={{ scaleY: 0, opacity: 1 }}
                      animate={{ scaleY: 1, opacity: 0 }}
                      transition={{ 
                        scaleY: { duration: 0.3, ease: 'easeOut' },
                        opacity: { duration: 0.5, delay: 0.3 }
                      }}
                      style={{ transformOrigin: 'center' }}
                    />

                    {/* Flash overlay */}
                    <motion.div
                      className="absolute inset-0 bg-[#C41E3A] z-40"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 0.3, 0] }}
                      transition={{ 
                        duration: 0.6,
                        times: [0, 0.3, 1],
                        delay: 0.2
                      }}
                    />

                    {/* White flash for transition */}
                    <motion.div
                      className="absolute inset-0 bg-white z-50"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: [0, 1] }}
                      transition={{ 
                        duration: 0.3,
                        delay: 0.6
                      }}
                    />
                  </>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      {/* Bottom Tagline - Desktop */}
      <motion.div 
        className="hidden md:block absolute bottom-0 left-0 right-0 z-20 px-6 py-6 text-center bg-gradient-to-t from-black/50 to-transparent"
        animate={{ 
          opacity: isTransitioning ? 0 : 1,
          y: isTransitioning ? 20 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-xs text-zinc-600 tracking-wider">
          FIX. RESTORE. DETAIL. — Complete automotive care under one roof.
        </p>
      </motion.div>

      {/* Mobile Bottom Bar */}
      <motion.div 
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 px-4 py-3 bg-black/90 backdrop-blur-md border-t border-zinc-900"
        animate={{ 
          opacity: isTransitioning ? 0 : 1,
          y: isTransitioning ? 20 : 0
        }}
        transition={{ duration: 0.3 }}
      >
        <p className="text-[10px] text-zinc-600 text-center tracking-wider">
          MA AFRICA FIX & DETAIL — One Vehicle. One Team. Complete Care.
        </p>
      </motion.div>
    </main>
  );
}
